import React, { useState } from 'react';
import { CurrentUserGq } from '../../../graphql/users/models/CurrentUserGq';
import VoxelTable from '../../../components/voxel/Tables/VoxelTable';
import VoxelTableCell from '../../../components/voxel/Tables/VoxelTableCell';
import VoxelTableHeadCell from '../../../components/voxel/Tables/VoxelTableHeadCell';
import VoxelTableRow from '../../../components/voxel/Tables/VoxelTableRow';
import { CurrentTeamGq } from '../../../graphql/users/models/CurrentTeamGq';
import { PagingInputGq } from '../../../graphql/users/models/PagingInputGq';
import { PagingResultGq } from '../../../graphql/users/models/PagingResultGq';
import VoxelTablePaginationDataSpecific from '../../../components/voxel/Tables/VoxelTablePaginationDataSpecific';
import VoxelIconButton from '../../../components/voxel/Buttons/VoxelIconButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/pro-solid-svg-icons';
import { usersApiService } from '../../../graphql/users/usersApiService';
import { ProjectGq } from '../../../graphql/projects/models/ProjectGq';
import { TeamProjectAccessGq } from '../../../graphql/users/models/TeamProjectAccessGq';
import VoxelAvatar from '../../../components/voxel/VoxelAvatar';
import VoxelText from '../../../components/voxel/Text/VoxelText';
import moment from 'moment';
import VoxelTooltipShort from '../../../components/voxel/Tooltip/VoxelTooltipShort';

export interface ButtonCellProps {
  team: CurrentTeamGq;
  endRow?: boolean; // This is required to keep rounding consistent when applying hover backgrounds etc.
  startRow?: boolean;
}

export interface Paging {
  page: number;
  amountPerPage: number;
}

export interface TeamAccessTableProps {
  user: CurrentUserGq;
  project: ProjectGq;
  teamProjectAccess?: TeamProjectAccessGq[];
  paging: PagingInputGq;
  pagingResult?: PagingResultGq;
  setPaging: React.Dispatch<React.SetStateAction<PagingInputGq>>;
  reload: () => Promise<any>;
}

export const TeamAccessTable: React.FC<TeamAccessTableProps> = ({
  teamProjectAccess,
  user,
  paging,
  pagingResult,
  setPaging,
  reload,
  project,
}) => {
  // Handle remove access
  const [loadingIds, setLoadingIds] = useState<number[]>([]);
  const editProjectAccessMutation = usersApiService.mutations.useEditProjectAccess();
  const handleRemoveAccess = (teamId: number) => {
    setLoadingIds((prev) => [...prev, teamId]);
    editProjectAccessMutation({
      teamId: teamId,
      removeProjectIds: [project.id],
      addProjectIds: [],
    })
      .then(reload)
      .finally(() => setLoadingIds((prev) => prev.filter((id) => id != teamId)));
  };

  return (
    <VoxelTable>
      <thead>
        <VoxelTableRow>
          <VoxelTableHeadCell className="pl-6">Team</VoxelTableHeadCell>
          <VoxelTableHeadCell align="center">Granted By</VoxelTableHeadCell>
          <VoxelTableHeadCell>Grant time</VoxelTableHeadCell>
          <VoxelTableHeadCell align="right" />
        </VoxelTableRow>
      </thead>
      <tbody>
        {teamProjectAccess?.map((pa, index) => {
          return (
            <VoxelTableRow key={pa.id}>
              <VoxelTableCell className="pl-6">
                <div className="flex flex-row items-center gap-4">
                  <VoxelAvatar name={pa.team.name} size="sm" />
                  <VoxelText variant="bread-normal" color="dark" noWrap>
                    {pa.team.name}
                  </VoxelText>
                </div>
              </VoxelTableCell>
              <VoxelTableCell align="center">
                {pa.accessGrantedByUser?.picture && (
                  <VoxelTooltipShort message={pa.accessGrantedByUser?.email}>
                    <img src={pa.accessGrantedByUser?.picture} className="rounded-full h-7 w-7" />
                  </VoxelTooltipShort>
                )}
              </VoxelTableCell>
              <VoxelTableCell>
                {moment.utc(pa.grantTime).local().format('MMM Do, YY')}
              </VoxelTableCell>
              <VoxelTableCell align="right">
                <div>
                  <VoxelIconButton
                    icon={<FontAwesomeIcon icon={faTimes} size="lg" />}
                    disabled={
                      loadingIds.includes(pa.id) ||
                      !(user.team.id === project.createdBy.team.id || user.team.id === pa.team.id)
                    }
                    color="white"
                    tooltip="Remove access"
                    onClick={(event) => {
                      event.stopPropagation();
                      event.preventDefault();
                      handleRemoveAccess(pa.team.id);
                    }}
                  />
                </div>
              </VoxelTableCell>
            </VoxelTableRow>
          );
        })}
        <VoxelTablePaginationDataSpecific
          pagingInput={paging}
          onPagingChange={(event, x) => setPaging(x)}
          pagingResult={pagingResult}
          colSpan={5}
        />
      </tbody>
    </VoxelTable>
  );
};

export default TeamAccessTable;
